import {
  FC, useCallback,
} from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames/bind';
import Icon from 'src/common/components/IconDS22';
import { useSearchInputContext, type SearchRefs } from 'src/common/components/Search/SearchInputProvider';
import ClearButton from 'src/common/components/Search/Inputs/ClearButton';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import type { SearchHeroProps } from 'src/common/components/Search/SearchHero';
import styles from '../Inputs.module.scss';

const cx = classNames.bind(styles);
export type InputDatesProps = {
  closeIconId?: 'x' | 'x-circle';
  variant?: SearchHeroProps['variant'];
  refs?: SearchRefs;
};

const InputDates: FC<InputDatesProps> = ({
  closeIconId = 'x',
  variant,
  refs,
}) => {
  const {
    filterDatesFormatted, filterDates, setFilterDates, submitSearch,
  } = useSearchInputContext();
  const {
    close, opening, isDatesOpen, openDates,
  } = useSearchHeroModalContext();

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
      close();
    }
  }, [close, submitSearch]);

  const onClear = useCallback(() => {
    setFilterDates([]);
    setTimeout(() => { refs?.when.current?.focus(); }, 0);
  }, [refs?.when, setFilterDates]);

  const { t } = useTranslation('common');

  const showIcon = variant === 'hero' || variant === 'sheet';
  const showClear = isDatesOpen
    && (!!filterDatesFormatted || filterDates.length > 0);

  return (
    <label
      className={cx(styles.label, `${variant}Variant`, {
        isOpen: isDatesOpen || opening === 'dates',
      })}
      tabIndex={-1}
    >
      {showIcon && (
        <div className={styles.icon}>
          <Icon
            id="calendar"
            size="l"
            data-test="when"
          />
        </div>
      )}
      <input
        readOnly
        className={cx(styles.input, `${variant}Variant`, { showIcon, showClear })}
        ref={refs?.when}
        onFocus={openDates}
        onClick={openDates}
        onKeyDown={onKeyDown}
        placeholder={t('search--search-when-placeholder')}
        value={filterDatesFormatted || ''}
        name="when-input"
        enterKeyHint="next"
        data-test="whenSearchInput"
        data-cwv="search--when"
      />
      {showClear && (
        <ClearButton
          onClick={onClear}
          closeIconId={closeIconId}
        />
      )}
    </label>

  );
};

export default InputDates;
